import { Injectable } from '@angular/core';
import { ApiService, HttpMethod } from '../api/api.service';
import { UsersService, GetUsersResponse, User } from '../users/users.service';
import { AdvisorHouseholdsResponse } from './advisor-households.model';

@Injectable({
  providedIn: 'root'
})
export class AdvisorHouseholdsService {
  private readonly baseUrl = 'advisor-households';

  constructor(
    private apiService: ApiService,
    private usersService: UsersService
  ) { }

  async getAdvisorHouseholds(
    advisorId: number,
    limit: number = 25,
    offset: number = 0
  ): Promise<AdvisorHouseholdsResponse> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('offset', offset.toString());
    return this.apiService.call<AdvisorHouseholdsResponse>(
      HttpMethod.GET,
      `${this.baseUrl}/advisor/${advisorId}?${params.toString()}`
    );
  }

  /**
   * Fetch households assigned to the currently logged-in advisor.
   * Resolves the advisor's USER_ID from `UsersService.getCurrentUser()`
   * and proxies through `getAdvisorHouseholds`.
   */
  async getMyAssignedHouseholds(
    limit: number = 25,
    offset: number = 0
  ): Promise<AdvisorHouseholdsResponse> {
    const me = await this.usersService.getCurrentUser();
    const advisorId = me?.USER?.USER_ID;
    if (!advisorId) {
      throw new Error('Unable to resolve current advisor USER_ID');
    }
    return this.getAdvisorHouseholds(advisorId, limit, offset);
  }

  /**
   * Fetch all advisors assigned to a specific household.
   * GET /advisor-households/household/{householdId}/advisors
   */
  async getAdvisorsForHousehold(householdId: number): Promise<User[]> {
    const response = await this.apiService.call<{ data: { advisors: User[] } }>(
      HttpMethod.GET,
      `${this.baseUrl}/household/${householdId}/advisors`
    );
    return response.data.advisors;
  }

  /**
   * Fetch all advisors available to assign (role=ADVISOR, optional name search).
   */
  async getAllAdvisors(search?: string): Promise<User[]> {
    const params = new URLSearchParams();
    params.append('role', 'ADVISOR');
    if (search) {
      params.append('search', search);
    }
    const response = await this.apiService.call<GetUsersResponse>(
      HttpMethod.GET,
      `users?${params.toString()}`
    );
    return response.users;
  }

  async assignAdvisorToHousehold(advisorId: number, householdId: number): Promise<unknown> {
    const body = { ADVISOR_ID: advisorId, HOUSEHOLD_ID: householdId };
    return this.apiService.call<unknown>(HttpMethod.POST, this.baseUrl, body);
  }

  async unassignAdvisorFromHousehold(advisorId: number, householdId: number): Promise<void> {
    return this.apiService.call<void>(
      HttpMethod.DELETE,
      `${this.baseUrl}/${advisorId}/${householdId}`
    );
  }
}
